import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { cn } from './internal/utils'

const SIZE_CLASSES = {
  small: 'size-4',
  default: 'size-6',
  large: 'size-8',
}

const Indicator = ({ size = 'default', className }) => (
  <Loader2
    role="status"
    aria-label="Chargement"
    className={cn(
      'ui-spin-dot animate-spin text-[var(--primary)]',
      SIZE_CLASSES[size] || SIZE_CLASSES.default,
      className
    )}
  />
)

/**
 * antd Spin. Standalone when childless, and an overlay wrapper when it has
 * children. `delay` is honored so fast loads do not flash a spinner.
 */
const Spin = ({
  spinning = true,
  size = 'default',
  tip,
  delay = 0,
  indicator,
  fullscreen = false,
  wrapperClassName,
  className,
  style,
  children,
  ...rest
}) => {
  const [visible, setVisible] = useState(delay === 0 ? spinning : false)

  useEffect(() => {
    if (!delay) {
      setVisible(spinning)
      return undefined
    }
    if (!spinning) {
      setVisible(false)
      return undefined
    }
    const timer = setTimeout(() => setVisible(true), delay)
    return () => clearTimeout(timer)
  }, [delay, spinning])

  const spinner = (
    <span
      className={cn('ui-spin inline-flex flex-col items-center gap-2', !visible && 'hidden', className)}
      style={style}
      {...rest}
    >
      {indicator ?? <Indicator size={size} />}
      {tip ? <span className="ui-spin-text text-sm text-[var(--muted-foreground)]">{tip}</span> : null}
    </span>
  )

  if (fullscreen) {
    if (!visible) return null
    return (
      <div className="ui-spin-fullscreen fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-[var(--overlay)]">
        {spinner}
      </div>
    )
  }

  if (!children) return spinner

  return (
    <div className={cn('ui-spin-nested-loading relative', wrapperClassName)}>
      {visible ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-[color-mix(in_oklab,var(--card)_60%,transparent)]">
          {spinner}
        </div>
      ) : null}
      <div
        className={cn('ui-spin-container', visible && 'pointer-events-none select-none opacity-50')}
        aria-busy={visible || undefined}
      >
        {children}
      </div>
    </div>
  )
}

Spin.setDefaultIndicator = () => {}

export { Indicator }
export default Spin
